// cart.js — depends on shared.js (api, dom, debounce, CustomHTMLElement, CART_ROUTES, isDev, updateGlobalCartCount)

// Cart-specific DOM helper (not shared — uses cart section, not drawer section)
const cartDom = {
  updateCartWrapper: async () => {
    try {
      const response = await api.get('/?section_id=cart');
      const cartWrapper = dom.query('#cart-wrapper');
      if (cartWrapper) {
        // Safe: response.data is from Shopify's own section rendering API (trusted source)
        cartWrapper.innerHTML = response.data;
      }
    } catch (error) {
      if (isDev) console.error('Failed to update cart wrapper:', error);
    }
  },
};

class MembershipAtc extends CustomHTMLElement {
  static MEMBERSHIP_VARIANT_ID = 45052730966302;

  async onClick(event) {
    try {
      await api.post(CART_ROUTES.add, {
        id: MembershipAtc.MEMBERSHIP_VARIANT_ID,
        quantity: 1,
      });

      await cartDom.updateCartWrapper();
    } catch (error) {
      if (isDev) console.error('Could not add membership to cart:', error);
    }
  }
}

class AdjustQuantityButton extends CustomHTMLElement {
  constructor() {
    super();
    this.debouncedAdjust = debounce(this.adjustQuantity.bind(this), 300);
  }

  onClick(event) {
    const operation = this.classList.contains('minus') ? 'minus' : 'plus';
    this.debouncedAdjust(operation);
  }

  async adjustQuantity(operation) {
    const lineItem = this.closest('li[data-key]');
    const variantKey = lineItem?.getAttribute('data-key');

    if (!variantKey) {
      if (isDev) console.error('No variant key found');
      return;
    }

    const spinner = lineItem.querySelector('.loading-spinner');
    const quantityInput = lineItem.querySelector('.global-cart-item-quantity');

    if (!quantityInput) {
      if (isDev) console.error('No quantity input found');
      return;
    }

    dom.toggleSpinner(spinner, true);

    try {
      const currentQuantity = parseInt(quantityInput.value) || 0;
      const newQuantity =
        operation === 'minus' ? currentQuantity - 1 : currentQuantity + 1;

      if (newQuantity <= 0) {
        await this.removeItem(lineItem, variantKey);
        return;
      }

      const response = await api.change(CART_ROUTES.change, {
        id: variantKey,
        quantity: newQuantity,
      });

      // Update UI
      quantityInput.value = newQuantity;
      updateGlobalCartCount(response.data.item_count);

      // Update price
      const updatedItem = response.data.items.find(
        (item) => item.key === variantKey,
      );
      if (updatedItem) {
        const priceElement = lineItem.querySelector('.line-item-price');
        if (priceElement) {
          priceElement.textContent = formatMoney(
            updatedItem.final_line_price,
            '${{amount}}',
          );
        }
      }

      await cartDom.updateCartWrapper();
    } catch (error) {
      if (isDev) console.error('Could not update item quantity:', error);
    } finally {
      dom.toggleSpinner(spinner, false);
    }
  }

  async removeItem(lineItem, variantKey) {
    try {
      const response = await api.change(CART_ROUTES.change, {
        id: variantKey,
        quantity: 0,
      });

      updateGlobalCartCount(response.data.item_count);
      await cartDom.updateCartWrapper();
    } catch (error) {
      if (isDev) console.error('Could not remove item from cart:', error);
    }
  }
}

class RemoveItemButton extends CustomHTMLElement {
  async onClick(event) {
    const lineItem = this.closest('[data-key]');
    const variantKey = lineItem?.getAttribute('data-key');

    if (!variantKey) {
      if (isDev) console.error('No variant key found for removal');
      return;
    }

    await this.handleRemoveItem(lineItem, variantKey);
  }

  async handleRemoveItem(item, variantKey) {
    try {
      const response = await api.change(CART_ROUTES.change, {
        id: variantKey,
        quantity: 0,
      });

      updateGlobalCartCount(response.data.item_count);
      await cartDom.updateCartWrapper();
    } catch (error) {
      if (isDev) console.error('Could not remove item from cart:', error);
    }
  }
}

// Register custom elements
if (!customElements.get('membership-atc'))
  customElements.define('membership-atc', MembershipAtc);
if (!customElements.get('adjust-quantity-button'))
  customElements.define('adjust-quantity-button', AdjustQuantityButton);
if (!customElements.get('remove-item-button'))
  customElements.define('remove-item-button', RemoveItemButton);

// Popover functionality with improved event handling
class PopoverManager {
  constructor() {
    this.popovers = dom.queryAll('.popover');
    this.infoButtons = dom.queryAll('.info-button');
    this.init();
  }

  init() {
    this.infoButtons.forEach((button) => {
      button.addEventListener('click', this.handleButtonClick.bind(this));
    });

    document.addEventListener('click', this.handleDocumentClick.bind(this));
  }

  handleButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const popover = button.nextElementSibling;

    if (!popover?.classList.contains('popover')) {
      return;
    }

    // Hide all other popovers
    this.hideAllPopovers();

    // Show clicked popover
    popover.classList.remove('hidden');
    popover.setAttribute('aria-expanded', 'true');
  }

  handleDocumentClick(event) {
    if (!event.target.matches('.info-button')) {
      this.hideAllPopovers();
    }
  }

  hideAllPopovers() {
    this.popovers.forEach((popover) => {
      popover.classList.add('hidden');
      popover.setAttribute('aria-expanded', 'false');
    });
  }

  destroy() {
    this.infoButtons.forEach((button) => {
      button.removeEventListener('click', this.handleButtonClick);
    });
    document.removeEventListener('click', this.handleDocumentClick);
  }
}

// Initialize popover manager when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PopoverManager();
  });
} else {
  new PopoverManager();
}
